import { useEffect } from 'react'
import { PersistGate } from 'redux-persist/integration/react'

import Routes from 'routes'
import getBaseUrl from 'utils/helpers/apiHelper'
import useBroadcastChannel from 'utils/hooks/useBroadcastChannel'
import { persistor } from 'app/store'
import { SocketProvider } from 'app/socket/socketContext'
import { notificationChannel } from 'utils/constants'
import { useGetConfigMutation } from 'app/services/system.service'
import { useLazyRegisterClientQuery } from 'app/services/auth.service'

import Loading from 'components/spinner/Loading'
import FileModal from 'components/modals/FileModal'
import ProgressBar from 'components/progress-bar/ProgressBar'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import ConferenceAlert from 'components/conference-alert/ConferenceAlert'
import DropdownSocket from 'components/dropdown-socket/DropdownSocket'
import { getDataFromDB, setDataToDB } from 'utils/helpers/storageHelper'
import '@progress/kendo-theme-default/dist/all.css';
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { hashActions } from 'app/slices/hash.slice'
import { useNavigate } from 'react-router-dom'
import { systemActions } from 'app/slices/system.slice'
import { isNotificationSupported, initializeNotifications } from 'services/firebase.service'
import { useImageUrl } from 'utils/hooks/useLogoUrl'

function App() {
  const dispatch = useAppDispatch()
  const [config] = useGetConfigMutation()
  const [registerClientQuery] = useLazyRegisterClientQuery()
  const systemSettings = useAppSelector((state) => state.system.data)
  const { imageUrl: faviconUrl } = useImageUrl(systemSettings?.imageUrlFavicon)

  useBroadcastChannel()

  useEffect(() => {
    getConfig()
    registerClient()
    notificationChannel.postMessage({ type: 'API_BASE_URL', payload: getBaseUrl() + 'api/' })
  }, [])

  useEffect(() => {
    const initializeFirebaseNotifications = async () => {
      try {
        if (isNotificationSupported()) {
          await initializeNotifications()
        } else {
          //console.log('Notifications are not supported in this browser.')
        }
      } catch (error) {
        //console.log('Error initializing notifications:', error)
      }
    }

    initializeFirebaseNotifications()
  }, [])

  // Update favicon when faviconUrl changes
  useEffect(() => {
    if (faviconUrl) {
      const faviconElement = document.getElementById('favicon') as HTMLLinkElement
      if (faviconElement) {
        faviconElement.href = faviconUrl
      }
    }
  }, [faviconUrl])

  const navigate = useNavigate()
  const setNameAndFavicon = (name: string) => {
    document.title = name
  }

  const getConfig = async () => {
    const {data :configData}  = (await config({})) as any //{ data: { salt: string } }
    if (!configData?.isSystemInit) {
      navigate('/init')
    }
    if (configData) {
      setNameAndFavicon(configData?.name)
      dispatch(systemActions.updateSystem(configData))
      setDataToDB('vimesoft', 'config', configData)
    }
  }

  const registerClient = async () => {
    const existingClientId = await getDataFromDB('vimesoft', 'clientId') as string | null;
    if (existingClientId && existingClientId.trim() !== '') return;
    try {
        const clientData = await registerClientQuery({}) as { data: { clientId: string } };

        if (clientData && clientData.data && clientData.data.clientId) {
            await setDataToDB('vimesoft', 'clientId', clientData.data.clientId);
            dispatch(hashActions.updateHashClientId(clientData.data.clientId));
        }
    } catch (error) {
        console.error("Error registering client", error);
    }
};



  return (
    <PersistGate loading={null} persistor={persistor}>
      <SocketProvider>
        <Routes />
        <ProgressBar />
        <DropdownAlert ref={(ref: DropdownAlert) => (DropdownAlert.ref = ref)} />
        <DropdownSocket ref={(ref: DropdownSocket) => (DropdownSocket.ref = ref)} />
        <ConferenceAlert />
        <FileModal ref={(ref: FileModal) => (FileModal.ref = ref)} />
        <Loading ref={(ref: Loading) => (Loading.ref = ref)} />
      </SocketProvider>
    </PersistGate>
  )
}

export default App
